import { Grid } from '@material-ui/core';
import { useQuoteField } from 'Quoting/components/hooks/useHandleQuoteFieldChange';
import { CheckboxInputGrid, MappingState } from 'Quoting/components/shared/CheckboxInputGrid';
import { FormRowGrid, GridRowProps } from 'Quoting/components/shared/GridRow';
import { ConditionalSection, QuoteFormConditionalSection } from 'Quoting/components/shared/QuoteFormConditionalSection';
import { QuoteFormElement } from 'Quoting/components/shared/QuoteFormElement';
import { FormWrapper } from 'Quoting/components/shared/QuoteFormWrapper';
import React from 'react';
import { useSelector } from 'react-redux';
import { ReducerState } from 'rootReducer';
import { isWellnessServicesDisabled, useBenefitToggle } from '../../PlanTemplates/CI/GVCI4Utilities';
import {
  getBenefitArrayPaths,
  getGCI5HealthScreeningServicesPaths,
  getParentSchedulePath,
} from '../Common/PropertyPaths';

const coverageIndex = 0;

const WaiverOfPremium = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const benefitName = 'waiverOfPremiumDetails';
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'waiver-of-permium-row-1'},
      items: [
        {
          key: 'waiver-of-permium-type',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CIWaiverOfPremiumBenefit.waiverTypeCd`}
              label="Type"
              value={getQuoteFieldValue<string>(additionalOptions.waiverTypeCd)}
              id={additionalOptions.waiverTypeCd}
              onChange={e => updateQuoteFieldValue(additionalOptions.waiverTypeCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'waiver-of-permium-waiting-period-days',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CIWaiverOfPremiumBenefit.waitingPeriod`}
              label="Waiting Period (Days)"
              value={getQuoteFieldValue<string>(additionalOptions.waitingPeriod)}
              id={additionalOptions.waitingPeriod}
              onChange={e => updateQuoteFieldValue(additionalOptions.waitingPeriod, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'waiver-of-permium-row-2'},
      items: [
        {
          key: 'waiver-of-permium-benefit-duration',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CIWaiverOfPremiumBenefit.benefitDuration`}
              label="Benefit Duration (Months)"
              value={getQuoteFieldValue<string>(additionalOptions.benefitDuration)}
              id={additionalOptions.benefitDuration}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitDuration, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SkinCancer = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const benefitName = 'skinCancerRider';

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}GVCI4Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );

  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'skin-cancer-row-1'},
      items: [
        {
          key: 'skin-cancer-type-of-rider',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CISkinCancerRider.benefitTypeCd`}
              label="Benefit Type"
              value={getQuoteFieldValue<string>(additionalOptions.benefitTypeCd)}
              id={additionalOptions.benefitTypeCd}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitTypeCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'skin-cancer-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)}
              id={additionalOptions.benefitAmount}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, parseInt(e.target.value))}
              adornment="money"
              //error={Boolean(skinCancerAmountError)}
              //helperText={skinCancerAmountError}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'skin-cancer-row-2'},
      items: [
        {
          key: 'skin-cancer-days',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CISkinCancerRider.timePeriod`}
              label="Time Period"
              value={getQuoteFieldValue<string>(additionalOptions.timePeriod)}
              id={additionalOptions.timePeriod}
              onChange={e => updateQuoteFieldValue(additionalOptions.timePeriod, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'skin-cancer-row-3'},
      items: [
        {
          key: 'skin-cancer-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'skin-cancer-row-4'},
      items: [
        {
          key: 'skin-cancer-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const SpecifiedDiseaseEnhancement = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();

  const benefitName = 'specConditionAndInfDiseaseDetails';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const getExclusionPath = (benefitName: string) =>
    getBenefitLabelMapping(
      `${benefitName}GVCI4Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    `${benefitName}GVCI4`,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'spec-cond-infectious-disease-row-1'},
      items: [
        {
          key: 'spec-cond-infectious-disease-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount (Recommended 25%)"
              type="number"
              value={getQuoteFieldValue<number>(additionalOptions.planBnfAmountPct)}
              id={additionalOptions.planBnfAmountPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.planBnfAmountPct, Number(e.target.value) / 100)}
              adornment="percentage"
              //error={isDefined(SpecConditionError)}
              //onBlur={() => {
              //  handleValidationOnBlur();
              //}}
              //helperText={SpecConditionError}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-cond-infectious-disease-row-2'},
      items: [
        {
          key: 'spec-cond-infectious-disease-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'spec-cond-infectious-disease-row-4'},
      items: [
        {
          key: 'spec-cond-infectious-disease-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SpecifiedChronicIllness = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'specifiedChronicIllnessRider';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}GVCI4Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'spec-chronic-illness-row-1'},
      items: [
        {
          key: 'spec-chronic-illness-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount (Recommended 50%)"
              type="number"
              value={getQuoteFieldValue<string>(additionalOptions.planBenefitAmountPct)}
              id={additionalOptions.planBenefitAmountPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.planBenefitAmountPct, e.target.value)}
              disabled
              adornment="percentage"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-row-2'},
      items: [
        {
          key: 'spec-chronic-illness-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-row-4'},
      items: [
        {
          key: 'spec-chronic-illness-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const SpecifiedChronicIllnessOrInjury = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'specifiedChronicIllnessAndAccidentalInjuryRider';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}GVCI4Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    benefitName,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-1'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="% of Plan Benefit Amount (Recommended 50%)"
              type="number"
              value={getQuoteFieldValue<string>(additionalOptions.planBenefitAmountPct)}
              id={additionalOptions.planBenefitAmountPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.planBenefitAmountPct, e.target.value)}
              disabled
              adornment="percentage"
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-2'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-3'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-InjuryPct',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Injury"
              value={getQuoteFieldValue<number>(additionalOptions.injuryPct)}
              id={additionalOptions.injuryPct}
              onChange={e => updateQuoteFieldValue(additionalOptions.injuryPct, e.target.value)}
              adornment="percentage"
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'spec-chronic-illness-accidental-injury-row-4'},
      items: [
        {
          key: 'spec-chronic-illness-accidental-injury-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const SecondEvaluation = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'secondEvaluationTransportationAndLodgingRider';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const secondEvaluation = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.secondEvaluation`
  );

  const coveredPersonNonLocalTransportation = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.coveredPersonNonLocalTransportation`
  );

  const companionNonLocalTransportation = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.companionNonLocalTransportation`
  );

  const coveredPersonLodging = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.coveredPersonLodging`
  );

  const companionLodging = getBenefitArrayPaths(
    selectedPlan?.planIndex,
    coverageIndex,
    `${benefitName}.companionLodging`
  );

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}GVCI4Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'second-evaluation-row-1'},
      items: [
        {
          key: 'second-evaluation-units',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="# of Units"
              options={[]}
              value={getQuoteFieldValue<number>(additionalOptions.numOfUnits)}
              id={additionalOptions.numOfUnits}
              onChange={e => updateQuoteFieldValue(additionalOptions.numOfUnits, e.target.value)}
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-2'},
      title: 'SECOND EVALUATION',
      items: [
        {
          key: 'second-evaluation-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount"
              type="number"
              value={getQuoteFieldValue<number>(secondEvaluation.benefitAmount)}
              id={secondEvaluation.benefitAmount}
              onChange={e => updateQuoteFieldValue(secondEvaluation.benefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-max',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Maximum Number (Consultation/Evaluations Per Treatment)"
              options={[]}
              value={getQuoteFieldValue<string>(secondEvaluation.maxNumberConsultationOrEvaluations)}
              id={secondEvaluation.maxNumberConsultationOrEvaluations}
              onChange={e => updateQuoteFieldValue(secondEvaluation.maxNumberConsultationOrEvaluations, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-3'},
      title: 'NON LOCAL TRANSPORTATION',
      items: [
        {
          key: 'second-evaluation-non-travel',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Non Local Travel Distance More Than (Miles)"
              options={[]}
              value={getQuoteFieldValue<string>(coveredPersonNonLocalTransportation.nonLocalTravelDistance)}
              id={coveredPersonNonLocalTransportation.nonLocalTravelDistance}
              onChange={e =>
                updateQuoteFieldValue(coveredPersonNonLocalTransportation.nonLocalTravelDistance, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-air-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Air Transportation Benefit Amount (Per Trip)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.airTransportationBenefitAmount)}
              id={coveredPersonNonLocalTransportation.airTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(
                  coveredPersonNonLocalTransportation.airTransportationBenefitAmount,
                  e.target.value
                )
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-ground-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Benefit Amount (Per Mile)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.groundTransportationBenefitAmount)}
              id={coveredPersonNonLocalTransportation.groundTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(
                  coveredPersonNonLocalTransportation.groundTransportationBenefitAmount,
                  e.target.value
                )
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-ground-transport-max',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Maximum Miles (Per Round Trip)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.groundTransportationMaxMiles)}
              id={coveredPersonNonLocalTransportation.groundTransportationMaxMiles}
              onChange={e =>
                updateQuoteFieldValue(coveredPersonNonLocalTransportation.groundTransportationMaxMiles, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Covered Person, Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonNonLocalTransportation.maxBenefitAmount)}
              id={coveredPersonNonLocalTransportation.maxBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(coveredPersonNonLocalTransportation.maxBenefitAmount, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(coveredPersonNonLocalTransportation.timePeriod)}
              id={coveredPersonNonLocalTransportation.timePeriod}
              onChange={e => updateQuoteFieldValue(coveredPersonNonLocalTransportation.timePeriod, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-4'},
      title: 'FAMILY MEMBER TRANSPORTATION',
      items: [
        {
          key: 'second-evaluation-companion-non-travel',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Non Local Travel Distance More Than (Miles)"
              options={[]}
              value={getQuoteFieldValue<string>(companionNonLocalTransportation.nonLocalTravelDistance)}
              id={companionNonLocalTransportation.nonLocalTravelDistance}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.nonLocalTravelDistance, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-air-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Air Transportation Benefit Amount (Per Trip)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.airTransportationBenefitAmount)}
              id={companionNonLocalTransportation.airTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.airTransportationBenefitAmount, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-ground-transport',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Benefit Amount (Per Mile)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.groundTransportationBenefitAmount)}
              id={companionNonLocalTransportation.groundTransportationBenefitAmount}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.groundTransportationBenefitAmount, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-ground-transport-max',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Ground Transportation Maximum Miles (Per Round Trip)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.groundTransportationMaxMiles)}
              id={companionNonLocalTransportation.groundTransportationMaxMiles}
              onChange={e =>
                updateQuoteFieldValue(companionNonLocalTransportation.groundTransportationMaxMiles, e.target.value)
              }
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(companionNonLocalTransportation.maxBenefitAmount)}
              id={companionNonLocalTransportation.maxBenefitAmount}
              onChange={e => updateQuoteFieldValue(companionNonLocalTransportation.maxBenefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(companionNonLocalTransportation.timePeriod)}
              id={companionNonLocalTransportation.timePeriod}
              onChange={e => updateQuoteFieldValue(companionNonLocalTransportation.timePeriod, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-5'},
      title: 'OUTPATIENT LODGING',
      items: [
        {
          key: 'second-evaluation-lodging-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonLodging.benefitAmount)}
              id={coveredPersonLodging.benefitAmount}
              onChange={e => updateQuoteFieldValue(coveredPersonLodging.benefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-lodging-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Covered Person, Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(coveredPersonLodging.maxBenefitAmount)}
              id={coveredPersonLodging.maxBenefitAmount}
              onChange={e => updateQuoteFieldValue(coveredPersonLodging.maxBenefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-lodging-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(coveredPersonLodging.timePeriod)}
              id={coveredPersonLodging.timePeriod}
              onChange={e => updateQuoteFieldValue(coveredPersonLodging.timePeriod, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-6'},
      title: 'FAMILY MEMBER LODGING',
      items: [
        {
          key: 'second-evaluation-companion-lodging-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Benefit Amount (Per Day)"
              type="number"
              value={getQuoteFieldValue<number>(companionLodging.benefitAmount)}
              id={companionLodging.benefitAmount}
              onChange={e => updateQuoteFieldValue(companionLodging.benefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-lodging-max-benefit',
          element: (
            <QuoteFormElement
              elementType="Text"
              label="Maximum Benefit Amount (Per Time Period)"
              type="number"
              value={getQuoteFieldValue<number>(companionLodging.maxBenefitAmount)}
              id={companionLodging.maxBenefitAmount}
              onChange={e => updateQuoteFieldValue(companionLodging.maxBenefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'second-evaluation-companion-lodging-time-period',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(companionLodging.timePeriod)}
              id={companionLodging.timePeriod}
              onChange={e => updateQuoteFieldValue(companionLodging.timePeriod, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'second-evaluation-row-7'},
      items: [
        {
          key: 'second-evaluation-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

export const LifeStyleEnhancement = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'lifestyleEnhancementRider';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const getExclusionPath = (benefitName: string) => {
    return getBenefitLabelMapping(
      `${benefitName}GVCI4Exclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );
  };
  const additionalOptions = getBenefitArrayPaths(selectedPlan?.planIndex, coverageIndex, benefitName);

  const benefitPaths: MappingState[] = getBenefitLabelMapping(
    `${benefitName}GVCI4`,
    `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`
  );

  const otherBenefitPath = `${getParentSchedulePath(
    selectedPlan?.planIndex,
    coverageIndex
  )}.${benefitName}.isOtherEligiblePrograms`;

  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'lifestyle-enhancement-row-1'},
      items: [
        {
          key: 'lifestyle-enhancement-benefit-amount',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="Benefit Amount"
              value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)}
              id={additionalOptions.benefitAmount}
              onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'lifestyle-enhancement-days',
          element: (
            <QuoteFormElement
              elementType="Select"
              label="Time Period"
              options={[]}
              value={getQuoteFieldValue<string>(additionalOptions.timePeriod)}
              id={additionalOptions.timePeriod}
              onChange={e => updateQuoteFieldValue(additionalOptions.timePeriod, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'lifestyle-enhancement-row-2'},
      items: [
        {
          key: 'lifestyle-enhancement-options',
          element: <CheckboxInputGrid state={benefitPaths} gridLength={3} />,
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'lifestyle-enhancement-row-3'},
      items: [
        {
          key: 'lifestyle-enhancement-other-option',
          element: getQuoteFieldValue<boolean>(otherBenefitPath) && (
            <QuoteFormElement
              elementType="Text"
              label="Additional Eligible Programs"
              value={getQuoteFieldValue<string>(additionalOptions.additionalEligiblePrograms)}
              id={additionalOptions.additionalEligiblePrograms}
              onChange={e => updateQuoteFieldValue(additionalOptions.additionalEligiblePrograms, e.target.value)}
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {key: 'lifestyle-enhancement-row-4'},
      items: [
        {
          key: 'lifestyle-enhancement-exclusions',
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />,
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const FixedBenefitCategoriesBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'healthScreeningDetails';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const additionalOptions = getGCI5HealthScreeningServicesPaths(selectedPlan?.planIndex, coverageIndex);
  const fixedPaths = getBenefitLabelMapping(
    `${benefitName}GVCI4FixedBenefit`,
    `${getParentSchedulePath(0, 0)}.${benefitName}`,
    {type: 'options', flag: 'isOptionApplied'}
  );

  return (
    <FormWrapper title="" withSeparator key="fixed-options">
      <Grid container spacing={2} style={{padding: '1rem'}}>
        <Grid item xs={12} md={6}>
          <QuoteFormElement
            elementType="Text"
            label="Benefit Amount (Per Unit)"
            type="number"
            value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)?.toFixed(2)}
            id={additionalOptions.benefitAmount}
            onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, Number(e.target.value))}
            adornment="money"
            disabled
          />
        </Grid>
        <FormWrapper title="Basic">
          <Grid item xs={12}>
            <CheckboxInputGrid state={fixedPaths} gridLength={3} />
          </Grid>
        </FormWrapper>
      </Grid>
    </FormWrapper>
  );
};

export const VariableBenefitCategoriesBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefit = 'healthScreeningDetails';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const paths = [0, 1, 2].map(catIndex =>
    getBenefitLabelMapping(
      `${benefit}GVCI4VariableCategory${catIndex}`,
      `${getParentSchedulePath(selectedPlan?.planIndex)}.${benefit}`,
      {
        type: 'options',
        flag: 'isOptionApplied',
      }
    )
  );

  const CategoryPaths = getBenefitLabelMapping(
    `${benefit}VariableOptions`,
    `${getParentSchedulePath(selectedPlan?.planIndex)}.${benefit}`,
    {type: 'optionCategories', flag: 'value'}
  );

  return (
    <>
      {Array.isArray(paths) &&
        paths.map((mappingState, index) => (
          <FormWrapper title={'Category ' + (index + 1)} collapsible withSeparator key={`cat-${index + 1}`}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <QuoteFormElement
                  elementType="Text"
                  label="Benefit Amount"
                  type="number"
                  value={getQuoteFieldValue<string>(CategoryPaths[index].path)}
                  id={CategoryPaths[index].path}
                  onChange={e => updateQuoteFieldValue(CategoryPaths[index].path, e.target.value)}
                  adornment="money"
                />
              </Grid>
              <FormWrapper title="Basic">
                <Grid item xs={12}>
                  <CheckboxInputGrid state={mappingState} gridLength={4} />
                </Grid>
              </FormWrapper>
            </Grid>
          </FormWrapper>
        ))}
    </>
  );
};

export const WellnessandPreventativeCareBenefitCategoriesBlock = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'healthScreeningDetails';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const additionalOptions = getGCI5HealthScreeningServicesPaths(selectedPlan?.planIndex, coverageIndex);

  const wellnessandpreventativecarepaths = getBenefitLabelMapping(
    `${benefitName}GVCI4WellnessandPreventiveCareBenefit`,
    `${getParentSchedulePath(selectedPlan?.planIndex)}.${benefitName}`,
    {type: 'options', flag: 'isOptionApplied'}
  );

  return (
    <FormWrapper title="" withSeparator key="wellness-preventative-care-options">
      <Grid container spacing={2} style={{padding: '1rem'}}>
        <Grid item xs={12} md={6}>
          <QuoteFormElement
            elementType="Text"
            label="Benefit Amount (Per Unit)"
            type="number"
            value={getQuoteFieldValue<number>(additionalOptions.benefitAmount)?.toFixed(2)}
            id={additionalOptions.benefitAmount}
            onChange={e => updateQuoteFieldValue(additionalOptions.benefitAmount, Number(e.target.value))}
            adornment="money"
            disabled
          />
        </Grid>
        <FormWrapper title="Basic">
          <Grid item xs={12}>
            <CheckboxInputGrid state={wellnessandpreventativecarepaths} gridLength={3} />
          </Grid>
        </FormWrapper>
      </Grid>
    </FormWrapper>
  );
};

const HealthScreeningServices = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue, getBenefitLabelMapping} = useQuoteField();
  const benefitName = 'healthScreeningDetails';
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);

  const getExclusionPath = (benefitName: string) =>
    getBenefitLabelMapping(
      `${benefitName}WellnessCIExclusions`,
      `${getParentSchedulePath(selectedPlan?.planIndex, coverageIndex)}.${benefitName}`,
      {
        type: 'exclusions',
        flag: 'isExclusion',
      }
    );

  const additionalOptions = getGCI5HealthScreeningServicesPaths(selectedPlan?.planIndex, coverageIndex);

  const exclusionPaths: MappingState[] = getExclusionPath(benefitName);
  const hashUnits = getQuoteFieldValue<number>(additionalOptions.healthScreeningHashUnits);
  const screenType = getQuoteFieldValue<string>(additionalOptions.healthScreeningType);

  const rows: GridRowProps[] = [
    {
      rowProps: {key: 'health-screening-row-1'},
      items: [
        {
          key: 'health-screening-type-of-benefit',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CiHealthScreeningBenefit.healthScreeningType`}
              label="Type of Benefit"
              value={getQuoteFieldValue<string>(additionalOptions.healthScreeningType)}
              id={additionalOptions.healthScreeningType}
              onChange={e => updateQuoteFieldValue(additionalOptions.healthScreeningType, e.target.value)}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'health-screening-hash-unit s',
          element: (
            <QuoteFormElement
              elementType="Text"
              type="number"
              label="# of Units"
              value={getQuoteFieldValue<number>(additionalOptions.healthScreeningHashUnits)}
              id={additionalOptions.healthScreeningHashUnits}
              onChange={e => updateQuoteFieldValue(additionalOptions.healthScreeningHashUnits, Number(e.target.value))}
              required
              disabled
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
        {
          key: 'health-screening-days',
          element: (
            <QuoteFormElement
              elementType="SelectV2"
              dropdownVariant="AttributeValue"
              searchKey={`CiHealthScreeningBenefit.timePeriodCd`}
              label="Time Period"
              value={getQuoteFieldValue<string>(additionalOptions.timePeriodCd)}
              id={additionalOptions.timePeriodCd}
              onChange={e => updateQuoteFieldValue(additionalOptions.timePeriodCd, e.target.value)}
              disabled
              required
            />
          ),
          props: {xs: 12, sm: 6, md: 6},
        },
      ],
    },
    {
      rowProps: {
        key: 'health-screening-row-2',
        hidden: hashUnits === undefined ? true : hashUnits < 1 || screenType !== 'Fixed' ? true : false,
      },
      items: [
        {
          key: 'health-screening-fixed-options',
          element: (
            <Grid container>
              <FixedBenefitCategoriesBlock />
            </Grid>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {
        key: 'health-screening-row-3',
        hidden: hashUnits === undefined ? true : hashUnits < 1 || screenType !== 'Variable' ? true : false,
      },
      items: [
        {
          key: 'health-screening-variable-options',
          element: (
            <Grid container>
              <VariableBenefitCategoriesBlock />
            </Grid>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {
        key: 'health-screening-row-3',
        hidden:
          hashUnits === undefined
            ? true
            : hashUnits < 1 || screenType !== 'Wellness and Preventative Care'
            ? true
            : false,
      },
      items: [
        {
          key: 'health-screening-wellnessandpreventativecare-options',
          element: (
            <Grid container>
              <WellnessandPreventativeCareBenefitCategoriesBlock />
            </Grid>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
    {
      rowProps: {key: 'health-screening-row-4'},
      items: [
        {
          key: 'health-screening-exclusions',
          hidden: true,
          element: (
            <FormWrapper title="EXCLUSIONS" collapsible withSeparator>
              <CheckboxInputGrid state={exclusionPaths} gridLength={3} />
            </FormWrapper>
          ),
          props: {xs: 12, sm: 12, md: 12},
        },
      ],
    },
  ];

  return <FormRowGrid rows={rows} />;
};

const Portability = () => {
  return <FormRowGrid rows={[]} />;
};

export const AdditionalOptionsandBenefitsGVCI4Block = () => {
  const {getQuoteFieldValue, updateQuoteFieldValue} = useQuoteField();
  const {selectedPlan} = useSelector((state: ReducerState) => state.quoteForm);
  const {onToggleBenefitApply} = useBenefitToggle();
  const benefitSchedulePath = getParentSchedulePath(selectedPlan?.planIndex, coverageIndex);

  const situsStateCd = getQuoteFieldValue<string>('riskStateCd');

  const additionalOptNBenefits: ConditionalSection[] = [
    {
      Component: Portability,
      key: 'Portability',
      label: 'PORTABILITY (UNLIMITED)',
      disabled: true,
      hidden: false,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isPortabilityApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isPortabilityApplied`, visibility),
    },
    {
      Component: WaiverOfPremium,
      key: 'WaiverOfPremium',
      label: 'WAIVER OF PREMIUM',
      disabled: true,
      hidden: false,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isWaiverOfPremiumApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isWaiverOfPremiumApplied`, visibility),
    },
    {
      Component: SkinCancer,
      key: 'SkinCancer',
      label: 'SKIN CANCER',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSkinCancerRiderApplied`),
      onToggleVisibility: (visibility: boolean) => {
        //updateQuoteFieldValue(`${benefitSchedulePath}.isSkinCancerRiderApplied`, visibility),
        const benefitName = 'skinCancerRider';
        const riderPath = `${benefitSchedulePath}.${benefitName}`;
        onToggleBenefitApply(benefitName, `${benefitSchedulePath}.isSkinCancerRiderApplied`, visibility, riderPath);
      },
    },
    {
      Component: SpecifiedDiseaseEnhancement,
      key: 'SpecifiedDiseaseEnhancement',
      label: 'SPECIFIED DISEASE ENHANCEMENT',
      disabled: true,
      hidden: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSpecConditionAndInfDiseaseApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isSpecConditionAndInfDiseaseApplied`, visibility),
    },
    {
      Component: SpecifiedChronicIllness,
      key: 'SpecifiedChronicIllness',
      hidden: false,
      label: 'SPECIFIED CHRONIC ILLNESS (90 DAY ELIMINATION PERIOD)',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSpecifiedChronicIllnessRiderApplied`),
      onToggleVisibility: (visibility: boolean) => {
        //updateQuoteFieldValue(`${benefitSchedulePath}.isSpecifiedChronicIllnessRiderApplied`, visibility),
        const benefitName = 'specifiedChronicIllnessRider';
        const riderPath = `${benefitSchedulePath}.${benefitName}`;
        onToggleBenefitApply(
          benefitName,
          `${benefitSchedulePath}.isSpecifiedChronicIllnessRiderApplied`,
          visibility,
          riderPath
        );
      },
    },
    {
      Component: SpecifiedChronicIllnessOrInjury,
      key: 'SpecifiedChronicIllnessOrInjury',
      hidden: false,
      label: 'SPECIFIED CHRONIC ILLNESS OR INJURY (365 DAY ELIMINATION PERIOD)',
      disabled: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied`),
      onToggleVisibility: (visibility: boolean) => {
        //updateQuoteFieldValue(`${benefitSchedulePath}.isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied`, visibility),
        const benefitName = 'specifiedChronicIllnessAndAccidentalInjuryRider';
        const riderPath = `${benefitSchedulePath}.${benefitName}`;
        onToggleBenefitApply(
          benefitName,
          `${benefitSchedulePath}.isSpecifiedChronicIllnessAndAccidentalInjuryRiderApplied`,
          visibility,
          riderPath
        );
      },
    },
    {
      Component: HealthScreeningServices,
      key: 'HealthScreeningServices',
      label: 'WELLNESS SERVICES',
      // configured via Kraken rule in Access360
      disabled: true,
      isApplied: isWellnessServicesDisabled(situsStateCd)
        ? false
        : getQuoteFieldValue(`${benefitSchedulePath}.isHealthScreeningApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isHealthScreeningApplied`, visibility),
    },
    {
      Component: LifeStyleEnhancement,
      key: 'LifeStyleEnhancement',
      disabled: true,
      hidden: true,
      label: 'LIFESTYLE ENHANCEMENT',
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isLifestyleEnhancementRiderApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(`${benefitSchedulePath}.isLifestyleEnhancementRiderApplied`, visibility),
    },
    {
      Component: SecondEvaluation,
      key: 'secondEvaluation',
      disabled: true,
      hidden: true,
      isApplied: getQuoteFieldValue(`${benefitSchedulePath}.isSecondEvaluationTransportationAndLodgingRiderApplied`),
      onToggleVisibility: (visibility: boolean) =>
        updateQuoteFieldValue(
          `${benefitSchedulePath}.isSecondEvaluationTransportationAndLodgingRiderApplied`,
          visibility
        ),
      label: 'SECOND EVALUATION, TRANSPORTATION, AND LODGING',
    },
  ];

  return (
    <FormWrapper title="Additional Options and Benefits" collapsible withSeparator>
      <QuoteFormConditionalSection sections={additionalOptNBenefits} />
    </FormWrapper>
  );
};
